import * as React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Typography,
  ListItemText,
  Checkbox,
  OutlinedInput
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import DeleteButton from '../components/common/DeleteButton';
import EditModal from '../components/common/EditModal';
import Tooltip from '../components/common/Tooltip';

interface Column {
  id: keyof Data | 'action';
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Data {
  officeIDName: string;
  'Country / Market': string;
  customerGroup: string;
  supplier: string;
  currency: string;
  officeID: string;
  maxHits: string;
  Cap: string;
  status: string;
}

const columns: readonly Column[] = [
  { id: 'officeIDName', label: 'Office ID Name', minWidth: 150 },
  { id: 'Country / Market', label: 'Country / Market', minWidth: 150 },
  { id: 'customerGroup', label: 'Customer Group', minWidth: 150 },
  { id: 'supplier', label: 'Supplier', minWidth: 150 },
  { id: 'currency', label: 'Currency', minWidth: 150 },
  { id: 'officeID', label: 'Office ID', minWidth: 150 },
  { id: 'maxHits', label: 'Max Hits', minWidth: 100 },
  { id: 'Cap', label: 'Cap', minWidth: 100 },
  { id: 'status', label: 'Status', minWidth: 100 },
  { id: 'action', label: 'Action', minWidth: 100, align: 'center' },
];

function createData(
  officeIDName: string,
  CountryMarket: string,
  customerGroup: string,
  supplier: string,
  currency: string,
  officeID: string,
  maxHits: string,
  Cap: string,
  status: string
): Data {
  return {
    officeIDName,
    'Country / Market': CountryMarket,
    customerGroup,
    supplier,
    currency,
    officeID,
    maxHits,
    Cap,
    status,
  };
}

const initialRows: Data[] = [
  createData('ID001', 'USA', 'Retail', 'Supplier A', '$10 USD', 'OFF123', '1000', '5000', 'Active'),
  createData('ID002', 'UK', 'Wholesale', 'Supplier B', '£7.60', 'OFF456', '800', '4000', 'Inactive'),
  createData('ID003', 'India', 'Online', 'Supplier C', '₹1000', 'OFF789', '1200', '4500', 'Active'),
];

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProp = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as 'bottom',
      horizontal: 'left' as 'left',
    },
    transformOrigin: {
      vertical: 'top' as 'top',
      horizontal: 'left' as 'left',
    },
    getContentAnchorEl: null, // Important for older versions of MUI v4; can omit for MUI v5+
  };

const label = [
  'Office ID Name',
  'Country / Market',
  'Customer Group',
  'Supplier',
  'Currency',
  'Office ID',
  'Max Hits',
  'Cap',
  'Currency',
];

const FlightOfficeID = () => {
  const [rows, setRows] = React.useState<Data[]>(initialRows);
  const [filteredRows, setFilteredRows] = React.useState<Data[]>(initialRows);
  const [editOpen, setEditOpen] = React.useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterBy, setFilterBy] = React.useState('');
const [labelName, setLabelName] = React.useState<string[]>([]);
  const labelChange = (event: SelectChangeEvent<typeof labelName>) => {
        const {
          target: { value },
        } = event;
        setLabelName(
          // On autofill we get a stringified value.
          typeof value === 'string' ? value.split(',') : value,
        );
      };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterBy(event.target.value);
  };

  const handleSearch = () => {
    if (!filterBy || !searchTerm) {
      setFilteredRows(rows);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = rows.filter((row) =>
      String(row[filterBy as keyof Data]).toLowerCase().includes(lowerSearch)
    );
    setFilteredRows(filtered);
  };

  const handleEditSave = (updatedData: Data) => {
    if (selectedRowIndex === null) return;
    const updatedRows = [...rows];
    updatedRows[selectedRowIndex] = updatedData;
    setRows(updatedRows);
    setFilteredRows(updatedRows);
    setEditOpen(false);
  };

  const handleDeleteRow = (id: string) => {
    const updatedRows = rows.filter((row) => row.officeID !== id);
    setRows(updatedRows);
    setFilteredRows(updatedRows);
  };

  const renderStatusButton = (status: string) => (
    <Button
      variant="contained"
      size="small"
      sx={{
        backgroundColor: status === 'Active' ? '#1cad2d' : '#e31a24',
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: status === 'Active' ? '#2e7d32' : '#c62828',
        },
      }}
    >
      {status}
    </Button>
  );

  const renderActions = (row: Data, index: number) => (
    <>
      <IconButton
        color="primary"
        size="small"
        onClick={() => {
          setSelectedRowIndex(index);
          setEditOpen(true);
        }}
      >
        <EditIcon />
      </IconButton>
      <DeleteButton
        onDelete={() => handleDeleteRow(row.officeID)}
        confirmMessage="Do you really want to delete this item?"
      />
    </>
  );

  React.useEffect(() => {
    setFilteredRows(rows);
  }, [rows]);

  return (
    <>
      <Box className="headingBtnWrap" sx={{ mb: 3 }}>
            <Typography variant="h1">Office ID</Typography>
              <CreateButtonWithModal modalTitle="Office ID">
                <Box className="modalFormWrap">
                  <Box component="form" className="modalForm">
                    <div className="form-control inputFieldWrap">
                      <label>Office ID Name</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Country / Market</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Customer Group</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Supplier</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Currency</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Office ID</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Max Hits</label>
                      <input type="text" className="inputField" />
                    </div>
                    <div className="form-control inputFieldWrap">
                      <label>Cap</label>
                      <input type="text" className="inputField" />
                    </div>
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <Button variant="contained" className="formBtn">
                      Save
                    </Button>
                  </Box>
                </Box>
                <BlackWhiteSwitch />
              </CreateButtonWithModal>
            </Box>
<Box className="searchInputWrap">
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%'}}
        className="searchInputFilterBox"
      >
        <FormControl sx={{ minWidth: 460 }}>
           <Select
            className='leftFilterBox'
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={labelName}
            onChange={labelChange}
            displayEmpty
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'Select';  // <-- Placeholder text
              }
              return selected.join(', ');
            }}
            MenuProps={MenuProp}
          >
            <MenuItem disabled value="">
            Select
            </MenuItem>
            {label.map((name) => (
              <MenuItem key={name} value={name} className='selectDropdown'>
                <Checkbox checked={labelName.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {/* <TextField
          className="leftSearchFilter"
          fullWidth
          variant="outlined"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                onClick={handleSearch}
              >
                Search
              </Button>
            ),
          }}
        /> */}
        <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                Search
              </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }} className="dynamicInputFieldWrap">
  {labelName.map((item) => (
    <div key={item} className="form-control inputFieldWrap">
      <label style={{ marginBottom: '4px', fontWeight: '500' }}>{item}</label>
      <input
        type="text"
        // placeholder={`Enter ${item}`}
        className="inputField"
      />
    </div>
  ))}
</Box>
    </Box>
      {/* <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}
        className="searchFilterDropdownWrap"
      >
        <TextField
          fullWidth
          variant="outlined"
          className="searchFilterInput"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 180 }}>
          <Select
            value={filterBy}
            onChange={handleFilterChange}
            displayEmpty
            className="searchFilterDropdown"
          >
            <MenuItem value=""><em>Select Filter</em></MenuItem>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="ID">ID</MenuItem>
            <MenuItem value="services">Services</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="createdBy">Created By</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleSearch} className="searchBtn">
          Search
        </Button>
      </Box> */}

      <Paper sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 100 }} align="center">Audit Trail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow hover key={row.officeID}>
                    {columns.map((column) => {
                      if (column.id === 'status') {
                        return (
                          <TableCell key={column.id}>
                            {renderStatusButton(row.status)}
                          </TableCell>
                        );
                      }
                      if (column.id === 'action') {
                        return (
                          <TableCell key={column.id} align="center">
                            {renderActions(row, rowIndex)}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={column.id}>
                          {row[column.id as keyof Data]}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Tooltip />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {selectedRowIndex !== null && (
        <EditModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={rows[selectedRowIndex]}
          onSave={handleEditSave}
          excludeFields={['action', 'status']}
          readOnlyFields={['createdBy']}
          title="Edit Office ID"
        />
      )}
    </>
  );
};

export default FlightOfficeID;
